import { type AxiosResponse } from 'axios';
import { BASE_URL } from '@avanta/shared/constants/common';
import { service } from '@avanta/shared/services/api';
import {
  type Speciality,
  type Appointment,
  type AppointmentPayload,
  type CancelAppointmentPayload,
  type EditAppointmentPayload,
  type Center,
  type SessionDetails,
  type DailySlot,
  type TopPsychologist,
  type BookingOptions,
  type ContractParams,
  type GetAppointmentsParams,
} from '@avanta/shared/types/appointments';
import { type Psychologist, type ContractType } from '@avanta/shared/types/psychologists';

const API_URL = `${BASE_URL}appointments`;

export type AppointmentServiceTypes = {
  getAppointments: (
    patientId: string,
    params?: GetAppointmentsParams
  ) => Promise<AxiosResponse<Appointment[]>>;
  cancel: ({
    psychologistId,
    appointmentId,
    patientId,
  }: CancelAppointmentPayload) => Promise<AxiosResponse<void>>;
  getSessionDetails: (sessionId: string) => Promise<AxiosResponse<SessionDetails>>;
  getCenterDetails: (centerId: number) => Promise<AxiosResponse<Center>>;
  book: (appointment: AppointmentPayload) => Promise<AxiosResponse<Appointment>>;
  edit: (appointment: EditAppointmentPayload) => Promise<AxiosResponse<Appointment>>;
  getSpecialities: (patientId: string) => Promise<AxiosResponse<Speciality[]>>;
  getCenters: (
    specialityId: string | number,
    params?: ContractParams
  ) => Promise<AxiosResponse<Center[]>>;
  getFreeHoursAvailability: (params: {
    patientId: string;
    specialityId: string | number;
    centerId: number | string;
    spokenLanguage: string;
    foreignFreelance?: boolean;
    contractId?: number;
    treatmentId?: number;
  }) => Promise<AxiosResponse<{ dailySlots: DailySlot[] }>>;
  getTopPsychologistsAvailability: (params: {
    startDate: string;
    days: number;
    specialityId: string | number;
    centerId: number | string;
    patientId: string;
    psychoFullName?: string;
    contractType?: ContractType;
    spokenLanguage?: string;
    slotHour?: string;
    contractId?: number;
    treatmentId?: number;
  }) => Promise<AxiosResponse<TopPsychologist[]>>;
  getPsychologistsByName: ({
    name,
    insurance,
    contractId,
    patientId,
  }: {
    name: string;
    insurance?: boolean;
    contractId?: string;
    patientId?: string;
  }) => Promise<AxiosResponse<Psychologist[]>>;
  getBookingOptionsByPsycho: ({
    psychologistId,
    insurance,
  }: {
    psychologistId: string;
    insurance?: boolean;
    contractId?: string;
    patientId?: string;
  }) => Promise<AxiosResponse<BookingOptions>>;
};

export const AppointmentService: AppointmentServiceTypes = {
  getAppointments: async (patientId, params) =>
    service.get(`${API_URL}/patients/${patientId}`, { params }),
  cancel: async body => service.post(`${API_URL}/cancel-from-patient`, body),
  getSessionDetails: async sessionId =>
    service.get(`${BASE_URL}appointmentSession/any/sessions/${sessionId}/patient`),
  getCenterDetails: async centerId => service.get(`${API_URL}/work-centers/${centerId}`),
  book: async appointment => await service.post(`${API_URL}/book`, appointment),
  edit: async appointment => service.post(`${API_URL}/change-from-patient`, appointment),
  getSpecialities: async patientId => service.get(`${API_URL}/specialities/patients/${patientId}`),
  getCenters: async (specialityId, params) =>
    service.get(`${API_URL}/work-centers/speciality/${specialityId}`, {
      params,
    }),
  getFreeHoursAvailability: async ({
    patientId,
    specialityId,
    centerId,
    spokenLanguage,
    foreignFreelance,
    contractId,
    treatmentId,
  }) =>
    service.get(`${API_URL}/availability/free-hours`, {
      params: {
        patientId,
        specialityId,
        centerId,
        spokenLanguage,
        foreignFreelance,
        contractId,
        treatmentId,
      },
    }),
  getTopPsychologistsAvailability: async ({
    startDate,
    days,
    specialityId,
    centerId,
    psychoFullName,
    spokenLanguage,
    contractType,
    patientId,
    slotHour,
    contractId,
    treatmentId,
  }) =>
    service.get(`${API_URL}/availability/top`, {
      params: {
        start: startDate,
        days,
        patientId,
        psychoFullName,
        specialityId,
        centerId,
        spokenLanguage,
        contractType,
        slotHour,
        contractId,
        treatmentId,
      },
    }),
  getPsychologistsByName: async ({ name, insurance, contractId, patientId }) =>
    service.get(`${API_URL}/psychologists/search`, {
      params: { ...(name ? { name } : {}), insurance, limit: 10, offset: 0, contractId, patientId },
    }),
  getBookingOptionsByPsycho: async ({ psychologistId, insurance, contractId, patientId }) =>
    service.get(`${API_URL}/psychologists/${psychologistId}/booking-options`, {
      params: { insurance, contractId, patientId },
    }),
};
